import React, { useState } from 'react';
import { Card, Button, Space, Collapse, Empty, Spin, message, Row, Col, Typography, Divider, theme, DatePicker, Input, Pagination, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { PlusOutlined, FileTextOutlined } from '@ant-design/icons';
import { useGetApplicationsQuery } from '../../store/api/applicationsApi';
import { useGetDocumentsByApplicationQuery } from '../../store/api/documentsApi';
import { useGetRoutesByApplicationQuery } from '../../store/api/routesApi';
import { useGetDocumentsByRouteQuery } from '../../store/api/documentsApi';
import UploadDocumentModal from '../../components/documents/UploadDocumentModal';
import DocumentsList from '../../components/documents/DocumentsList';
import DocumentRegistryTab from '../../components/documents/DocumentRegistryTab';
import { Application } from '../../types';
import { useGetApplicationDocumentsStatsQuery } from '../../store/api/documentsApi';
import {
  filterApplicationDocumentsForDisplay,
  filterRouteDocumentsForApplication,
} from '../../utils/documentUploadScope';
import {
  readDocumentsPageMainTab,
  writeDocumentsPageMainTab,
  type DocumentsPageMainTab,
} from '../../utils/documentRegistryUi';
import { PageLayout } from '../../ui';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const DocumentsPage: React.FC = () => {
  const { token } = theme.useToken();
  const [mainTab, setMainTab] = useState<DocumentsPageMainTab>(() => readDocumentsPageMainTab());
  // TabPane is deprecated in latest antd; keep Tabs API usage minimal
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<any | null>(null);
  const [activeKeys, setActiveKeys] = useState<string[] | number[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [activeTab, setActiveTab] = useState<'in_work' | 'completed'>('in_work');
  const [search, setSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<[any, any] | null>(null);

  const { data: applicationsData, isLoading: applicationsLoading, refetch: refetchApplications } = useGetApplicationsQuery(
    {
      page,
      limit,
      statusGroup: activeTab,
      search: search || undefined,
      dateFrom: dateRange?.[0]?.toISOString?.(),
      dateTo: dateRange?.[1]?.toISOString?.(),
    },
    { skip: mainTab !== 'applications' }
  );

  const handleMainTabChange = (key: string) => {
    const tab: DocumentsPageMainTab = key === 'applications' ? 'applications' : 'registry';
    setMainTab(tab);
    writeDocumentsPageMainTab(tab);
  };

  const handleUploadSuccess = () => {
    refetchApplications();
    message.success('Документ успешно загружен');
  };

  const handleUploadClick = (application: Application, route?: any) => {
    setSelectedApplication(application);
    setSelectedRoute(route || null);
    setUploadModalVisible(true);
  };

  const getApplicationTitle = (application: Application) => {
    return (
      <Space>
        <Text strong style={{ color: token.colorText }}>Заявка {application.externalNumber}</Text>
        <Text type="secondary" style={{ color: token.colorTextSecondary }}>от {application.client?.name}</Text>
        <Text type="secondary" style={{ color: token.colorTextSecondary }}>
          {new Date(application.applicationDate).toLocaleDateString('ru-RU')}
        </Text>
      </Space>
    );
  };

  // Компонент для отображения статистики заявки
  const ApplicationStatsWithRoutes: React.FC<{ application: Application }> = ({ application }) => {
    const { data: stats } = useGetApplicationDocumentsStatsQuery(application.id);

    const totalFilesCount = stats?.files || 0;
    const totalPhotosCount = stats?.photos || 0;
    const routesCount = stats?.routesCount || 0;

    return (
      <Space size="small">
        <Text type="secondary" style={{ color: token.colorTextQuaternary }}>|</Text>
        <Text type="secondary" style={{ color: token.colorTextSecondary }}>
          <FileTextOutlined /> {totalFilesCount} докум.
        </Text>
        <Text type="secondary" style={{ color: token.colorTextSecondary }}>
          📷 {totalPhotosCount} фото
        </Text>
        <Text type="secondary" style={{ color: token.colorTextSecondary }}>
          🚛 {routesCount} маршр.
        </Text>
        {stats && stats.routeDocuments > 0 && (
          <Text type="secondary" style={{ fontSize: '12px', color: token.colorTextQuaternary }}>
            (в маршр.: {stats.routeDocuments})
          </Text>
        )}
      </Space>
    );
  };

  // RouteDocumentsStats removed (unused) — keep codebase minimal until needed

  const ApplicationDocuments: React.FC<{ application: Application }> = ({ application }) => {
    const { data: documents, isLoading: documentsLoading, refetch: refetchDocuments } = useGetDocumentsByApplicationQuery(application.id);
    const { data: routes, isLoading: routesLoading } = useGetRoutesByApplicationQuery(application.id);
    const visibleApplicationDocuments = filterApplicationDocumentsForDisplay(documents);

    return (
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Text type="secondary" style={{ color: token.colorTextSecondary }}>
              Документов по заявке: {visibleApplicationDocuments.length}
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleUploadClick(application)}
            >
              Загрузить документ к заявке
            </Button>
          </Col>
        </Row>
        {documentsLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin />
          </div>
        ) : visibleApplicationDocuments.length > 0 ? (
          <DocumentsList
            documents={visibleApplicationDocuments}
            onRefresh={refetchDocuments}
            groupByClientCarrier
          />
        ) : (
          <Empty
            description="Нет документов по заявке"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
        <Divider style={{ borderColor: token.colorSplit }} />
        <Title level={5} style={{ color: token.colorText }}>Маршруты заявки</Title>
        {routesLoading ? (
          <Spin />
        ) : routes && routes.length > 0 ? (
          routes.map((route: any) => (
            <Card key={route.id} style={{ marginBottom: 16, backgroundColor: token.colorBgContainer, borderColor: token.colorBorder }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong style={{ color: token.colorText }}>Маршрут #{route.id}</Text>{' '}
                  <Text type="secondary" style={{ color: token.colorTextSecondary }}>{route.loadingAddress} → {route.unloadingAddress}</Text>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleUploadClick(application, route)}
                  >
                    Загрузить документ к маршруту
                  </Button>
                </Col>
              </Row>
              <RouteDocuments route={route} applicationId={application.id} />
            </Card>
          ))
        ) : (
          <Empty description="Нет маршрутов" />
        )}
      </div>
    );
  };

  // Компонент для документов маршрута
  const RouteDocuments: React.FC<{ route: any; applicationId: number }> = ({ route, applicationId }) => {
    const { data: documents, isLoading, refetch } = useGetDocumentsByRouteQuery(route.id);
    const visibleDocuments = filterRouteDocumentsForApplication(documents, applicationId);

    const photosCount = visibleDocuments.filter((doc) => doc.fileType === 'photo').length || 0;
    const filesCount = visibleDocuments.filter((doc) => doc.fileType === 'file').length || 0;
    
    return (
      <div style={{ marginTop: 12 }}>
        <Space size="small">
          <Text type="secondary" style={{ color: '#9ca3af' }}>
            <FileTextOutlined /> {filesCount} докум.
          </Text>
          <Text type="secondary" style={{ color: '#9ca3af' }}>
            📷 {photosCount} фото
          </Text>
        </Space>
        {isLoading ? (
          <Spin style={{ marginLeft: 16 }} />
        ) : visibleDocuments.length > 0 ? (
          <DocumentsList documents={visibleDocuments} onRefresh={refetch} groupByClientCarrier />
        ) : (
          <Empty description="Нет документов по маршруту" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>
    );
  };

  const applications = applicationsData?.applications || [];

  const applicationsTabContent =
    applicationsLoading ? (
      <div className="text-center py-12">
        <Spin size="large" />
      </div>
    ) : (
      <Card style={{ backgroundColor: token.colorBgContainer, borderColor: token.colorBorder }}>
        <Tabs activeKey={activeTab} onChange={(k) => { setActiveTab(k as any); setPage(1); setActiveKeys([]); }}>
          <Tabs.TabPane tab="В работе" key="in_work">
            {applications.length > 0 ? (
              <Collapse 
                activeKey={activeKeys}
                style={{ backgroundColor: token.colorBgContainer }}
                onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
              >
                {applications.map((application: Application) => (
                  <Panel
                    key={application.id}
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div>
                          {getApplicationTitle(application)}
                        </div>
                        <ApplicationStatsWithRoutes application={application} />
                      </div>
                    }
                    style={{ backgroundColor: token.colorBgContainer, borderColor: token.colorBorder }}
                  >
                    <ApplicationDocuments application={application} />
                  </Panel>
                ))}
              </Collapse>
            ) : (
              <Empty description="Нет заявок для отображения документов" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Tabs.TabPane>
          <Tabs.TabPane tab="Завершенные" key="completed">
            {applications.length > 0 ? (
              <Collapse 
                activeKey={activeKeys}
                style={{ backgroundColor: token.colorBgContainer }}
                onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
              >
                {applications.map((application: Application) => (
                  <Panel
                    key={application.id}
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div>
                          {getApplicationTitle(application)}
                        </div>
                        <ApplicationStatsWithRoutes application={application} />
                      </div>
                    }
                    style={{ backgroundColor: token.colorBgContainer, borderColor: token.colorBorder }}
                  >
                    <ApplicationDocuments application={application} />
                  </Panel>
                ))}
              </Collapse>
            ) : (
              <Empty description="Нет заявок для отображения документов" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Tabs.TabPane>
        </Tabs>

        <Row justify="end" style={{ marginTop: 16 }}>
          <Pagination
            size="small"
            current={applicationsData?.pagination?.page || page}
            pageSize={applicationsData?.pagination?.limit || limit}
            total={applicationsData?.pagination?.total || applications.length}
            onChange={(p, ps) => {
              setPage(p);
              setLimit(ps);
            }}
            showSizeChanger
          />
        </Row>
      </Card>
    );

  return (
    <PageLayout
      title={
        <Space>
          <FileTextOutlined />
          <span>Документы</span>
        </Space>
      }
      subtitle={
        mainTab === 'registry'
          ? 'Поиск документов по заявкам, маршрутам и типам'
          : 'Управление документами по заявкам и маршрутам'
      }
      actions={
        mainTab === 'applications' ? (
          <Space wrap size="middle">
            <Input
              allowClear
              placeholder="Поиск по номеру или клиенту"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{ width: 260 }}
            />
            <DatePicker.RangePicker
              value={dateRange as any}
              onChange={(range) => {
                setDateRange(range as any);
                setPage(1);
              }}
            />
            <Space.Compact>
              <Button onClick={() => setActiveKeys([])}>Свернуть все</Button>
              <Button onClick={() => setActiveKeys(applications.map((a: Application) => a.id))}>Развернуть все</Button>
            </Space.Compact>
          </Space>
        ) : undefined
      }
    >
      <Tabs activeKey={mainTab} onChange={handleMainTabChange}>
        <Tabs.TabPane tab="Реестр документов" key="registry">
          <DocumentRegistryTab />
        </Tabs.TabPane>
        <Tabs.TabPane tab="По заявкам" key="applications">
          {applicationsTabContent}
        </Tabs.TabPane>
      </Tabs>

      <UploadDocumentModal
        visible={uploadModalVisible}
        onCancel={() => {
          refetchApplications();
          setUploadModalVisible(false);
          setSelectedApplication(null);
          setSelectedRoute(null);
        }}
        onSuccess={handleUploadSuccess}
        application={selectedApplication || undefined}
        route={selectedRoute || undefined}
      />
    </PageLayout>
  );
};

export default DocumentsPage; 